import { Component, OnInit } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { ExpenseService } from '../../../../Services/Expenses/expense.service';
import { HttpParams } from '@angular/common/http';
import { NgFor, NgIf, NgClass, DecimalPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NotificationService } from '../../../../Services/notification.service';
import { FirmSelectionService } from '../../../../Services/firm-selection.service';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { environment } from '../../../../../environments/environment';
import { Chart, registerables } from 'chart.js';
Chart.register(...registerables);

@Component({
  selector: 'app-expense',
  standalone: true,
  imports: [RouterLink, NgFor, NgIf, NgClass, FormsModule, DecimalPipe],
  templateUrl: './expense.component.html',
  styleUrl: './expense.component.css'
})
export class ExpenseComponent implements OnInit {

  expenseData: any[] = [];   // Master list (API data)
  filteredData: any[] = [];  // Search result list
  paginatedData: any[] = []; // Display list (current page)

  totalExpense: number = 0;
  searchText: string = '';
  selectedPeriod: string = 'month'; // Default to this month
  addPath: string = '/add-expense';
  activeTab: 'history' | 'analytics' = 'history';

  // Analytics Data
  analyticsData: any = null;
  categoryChart: any;
  trendChart: any;

  // Pagination Variables
  currentPage: number = 1;
  itemsPerPage: number = 10;
  totalPages: number = 0;

  constructor(
    private expenseService: ExpenseService,
    private notificationService: NotificationService,
    private firmSelectionService: FirmSelectionService,
    private router: Router
  ) { }

  ngOnInit() {
    const urlTree = this.router.parseUrl(this.router.url);
    const tab = urlTree.queryParams['tab'];
    if (tab === 'analytics') {
      this.activeTab = 'analytics';
      this.loadAnalytics();
    } else {
      this.getExpenseData();
    }
    if (this.router.url.includes('rm-portal')) {
      this.addPath = '/rm-portal/add-expense';
    }
  }

  getExpenseData() {
    let params = new HttpParams();

    if (this.selectedPeriod && this.selectedPeriod !== 'all') {
      params = params.set('period', this.selectedPeriod);
    }

    this.expenseService.getExpenses(params).subscribe((response: any) => {
      this.expenseData = response.data || [];

      // Initialize filteredData with full data
      this.filteredData = [...this.expenseData];

      // Calculate initial logic
      this.calculateTotal();
      this.setupPagination();
    });
  }

  onPeriodChange() {
    this.currentPage = 1; // Reset to first page
    if (this.activeTab === 'history') {
      this.getExpenseData();
    } else {
      this.loadAnalytics();
    }
  }

  switchTab(tab: 'history' | 'analytics') {
    this.activeTab = tab;
    if (tab === 'history') {
      this.getExpenseData();
    } else {
      this.loadAnalytics();
    }
  }

  loadAnalytics() {
    let params = new HttpParams();
    this.expenseService.getExpenseAnalytics(params).subscribe((response: any) => {
      this.analyticsData = response.data;
      setTimeout(() => this.renderCharts(), 100);
    });
  }

  renderCharts() {
    if (!this.analyticsData) return;

    // Destroy existing charts
    if (this.categoryChart) this.categoryChart.destroy();
    if (this.trendChart) this.trendChart.destroy();

    // 1. Category Chart (Grouped Bar for Comparison)
    const catCtx = document.getElementById('categoryChart') as HTMLCanvasElement;
    if (catCtx) {
      this.categoryChart = new Chart(catCtx, {
        type: 'bar',
        data: {
          labels: this.analyticsData.category_comparison.map((c: any) => c.label),
          datasets: [
            {
              label: 'Current Month',
              data: this.analyticsData.category_comparison.map((c: any) => c.current),
              backgroundColor: '#C19725',
              borderColor: '#C19725',
              borderWidth: 1
            },
            {
              label: 'Last Month',
              data: this.analyticsData.category_comparison.map((c: any) => c.last),
              backgroundColor: '#E6C66C',
              borderColor: '#E6C66C',
              borderWidth: 1
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { position: 'bottom' }
          },
          scales: {
            y: {
              beginAtZero: true,
              ticks: {
                callback: (value) => '₹' + value
              }
            }
          }
        }
      });
    }

    // 2. Trend Chart (Bar/Line)
    const trendCtx = document.getElementById('trendChart') as HTMLCanvasElement;
    if (trendCtx) {
      this.trendChart = new Chart(trendCtx, {
        type: 'bar',
        data: {
          labels: this.analyticsData.monthly_trends.map((t: any) => t.month),
          datasets: [{
            label: 'Total Spending',
            data: this.analyticsData.monthly_trends.map((t: any) => t.total),
            backgroundColor: 'rgba(193, 151, 37, 0.7)',
            borderColor: '#C19725',
            borderWidth: 1
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            y: { beginAtZero: true }
          }
        }
      });
    }
  }

  /* ============================================================
     IMAGE PROXY HELPERS (Copied from StockListComponent)
  ============================================================ */
  proxyImageUrl(url: string): string {
    if (!url) return url;
    if (url.includes('/api/image-proxy/')) return url;

    // Detect if it's a storage URL
    const match = url.match(/\/storage\/(.+)$/);
    if (!match) return url;

    const backendBase = environment.apiBaseUrl.replace(/\/api\/$/, '');
    return `${backendBase}/api/image-proxy/${match[1]}`;
  }

  async loadImageBase64(url: string): Promise<string | null> {
    try {
      const proxiedUrl = this.proxyImageUrl(url);
      const res = await fetch(proxiedUrl);
      if (!res.ok) throw new Error(res.statusText);
      const blob = await res.blob();
      return await new Promise(resolve => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.readAsDataURL(blob);
      });
    } catch (err) {
      console.error('Image load FAILED:', url, err);
      return null;
    }
  }

  async preparePdfImages(): Promise<void> {
    for (const row of this.filteredData) {
      if (row.document && !row.document.toLowerCase().endsWith('.pdf')) {
        row.__img = await this.loadImageBase64(row.document);
      } else {
        row.__img = null;
      }
    }
  }

  // --- EXPORT LOGIC ---
  printExpenses() {
    const firmName = this.firmSelectionService.getselectedFirmName()?.name || 'My Firm';
    const periodName = (this.selectedPeriod || 'THIS MONTH').toUpperCase();
    const reportDate = new Date().toLocaleString('en-GB');

    const formatNum = (num: number) => {
      return (num || 0).toLocaleString('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    };

    let printHtml = `
      <html>
      <head>
        <title>Expense Report - ${firmName}</title>
        <style>
          body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; margin: 30px; font-size: 12px; color: #333; }
          .header { text-align: center; margin-bottom: 30px; border-bottom: 3px solid #C19725; padding-bottom: 10px; }
          .header h1 { margin: 0; color: #C19725; text-transform: uppercase; font-size: 24px; }
          .header p { margin: 5px 0; color: #666; font-size: 14px; font-weight: bold; }
          .info-bar { display: flex; justify-content: space-between; margin-bottom: 20px; font-weight: bold; border-bottom: 1px solid #eee; padding-bottom: 10px; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 20px; table-layout: fixed; }
          th, td { border: 1px solid #ddd; padding: 10px; text-align: left; word-wrap: break-word; }
          th { background-color: #f6f7fb; color: #C19725; font-size: 10px; text-transform: uppercase; letter-spacing: 1px; }
          .text-right { text-align: right; }
          .font-bold { font-weight: bold; }
          .footer { margin-top: 30px; display: flex; justify-content: flex-end; }
          .total-box { border: 2px solid #C19725; padding: 15px; min-width: 200px; text-align: right; background-color: #fffdf5; }
          .total-label { color: #C19725; font-size: 11px; font-weight: bold; text-transform: uppercase; }
          .total-amount { font-size: 20px; font-weight: bold; margin-top: 5px; color: #000; }
          @media print { 
            @page { margin: 15mm; } 
            body { margin: 0; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Expense Report</h1>
          <p>${firmName}</p>
        </div>
        <div class="info-bar">
          <div>PERIOD: ${periodName}</div>
          <div>GENERATED ON: ${reportDate}</div>
        </div>
        <table>
          <thead>
            <tr>
              <th style="width: 8%">SR. NO.</th>
              <th style="width: 15%">DATE</th>
              <th style="width: 25%">EXPENSE TYPE</th>
              <th style="width: 12%">BILL</th>
              <th style="width: 15%" class="text-right">AMOUNT</th>
            </tr>
          </thead>
          <tbody>
    `;

    this.filteredData.forEach((item, index) => {
      const docHtml = item.document
        ? (item.document.toLowerCase().endsWith('.pdf')
          ? '<span style="color:#bb0000; font-size: 10px;">PDF</span>'
          : `<img src="${item.document}" style="width:30px; height:30px; object-fit:cover; border-radius:4px; border:1px solid #ddd;" />`)
        : '-';

      printHtml += `
        <tr>
          <td style="text-align:center;">${index + 1}</td>
          <td style="text-align:center;">${item.date}</td>
          <td class="font-bold">${item.expense_type?.name || item.type}</td>
          <td style="text-align:center;">${docHtml}</td>
          <td class="text-right font-bold">₹ ${formatNum(item.amount)}</td>
        </tr>
      `;
    });

    if (this.filteredData.length === 0) {
      printHtml += `<tr><td colspan="6" style="text-align:center; padding: 30px; color: #999;">No expenses found for this selection.</td></tr>`;
    }

    printHtml += `
          </tbody>
        </table>
        <div class="footer">
          <div class="total-box">
            <div class="total-label">Grand Total</div>
            <div class="total-amount">₹ ${formatNum(this.totalExpense)}</div>
          </div>
        </div>
        <script>
          window.onload = function() {
            window.print();
            window.onafterprint = function() { window.close(); };
          }
        </script>
      </body>
      </html>
    `;

    const printWin = window.open('', '_blank');
    if (printWin) {
      printWin.document.open();
      printWin.document.write(printHtml);
      printWin.document.close();
    }
  }

  exportToExcel() {
    const firmName = this.firmSelectionService.getselectedFirmName()?.name || 'My Firm';
    const periodName = this.selectedPeriod.toUpperCase();
    const fileName = `Expenses_${firmName.replace(/\s+/g, '_')}_${periodName}.xlsx`;

    const dataToExport = this.filteredData.map((item, index) => ({
      'SR. NO.': index + 1,
      'DATE': item.date,
      'TYPE': item.expense_type?.name || item.type,
      'AMOUNT': Number(item.amount)
    }));

    // Create Worksheet
    const worksheet = XLSX.utils.json_to_sheet([]);

    // Add Header Rows
    XLSX.utils.sheet_add_aoa(worksheet, [
      [`${firmName.toUpperCase()} - EXPENSE REPORT`],
      [`PERIOD: ${periodName}`],
      [`GENERATED ON: ${new Date().toLocaleString('en-GB')}`],
      [`TOTAL EXPENSE: ₹ ${this.totalExpense.toLocaleString('en-IN')}`],
      [] // Spacer
    ], { origin: "A1" });

    // Add Table Data
    XLSX.utils.sheet_add_json(worksheet, dataToExport, { origin: "A6", skipHeader: false });

    // Set Column Widths
    worksheet['!cols'] = [
      { wch: 10 }, // Sr No
      { wch: 15 }, // Date
      { wch: 25 }, // Type
      { wch: 15 }, // Amount
      { wch: 40 }, // Description
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Expenses');

    XLSX.writeFile(workbook, fileName);
  }

  async exportToPDF() {
    const firmName = this.firmSelectionService.getselectedFirmName()?.name || 'My Firm';
    const periodName = this.selectedPeriod.toUpperCase();
    const reportDate = new Date().toLocaleString('en-GB');
    const fileName = `Expenses_${firmName.replace(/\s+/g, '_')}_${periodName}.pdf`;

    // 0. Preload Images
    this.notificationService.showSuccess("Preparing PDF with images...", "Please wait");
    await this.preparePdfImages();

    const doc = new jsPDF();

    // --- Header ---
    doc.setFontSize(22);
    doc.setTextColor(193, 151, 37); // #C19725
    doc.text('EXPENSE REPORT', 105, 20, { align: 'center' });

    doc.setFontSize(14);
    doc.setTextColor(100);
    doc.text(firmName, 105, 30, { align: 'center' });

    doc.setDrawColor(193, 151, 37);
    doc.setLineWidth(1);
    doc.line(20, 35, 190, 35);

    // --- Info Bar ---
    doc.setFontSize(10);
    doc.setTextColor(0);
    doc.setFont('helvetica', 'bold');
    doc.text(`PERIOD: ${periodName}`, 20, 45);
    doc.text(`GENERATED ON: ${reportDate}`, 190, 45, { align: 'right' });

    // --- Table ---
    // REMOVED DESCRIPTION, ADDED BILL IMAGE COLUMN
    const headers = [['SR.NO.', 'DATE', 'TYPE', 'BILL IMAGE', 'AMOUNT']];
    const data = this.filteredData.map((item, index) => [
      index + 1,
      item.date,
      item.expense_type?.name || item.type,
      '', // Placeholder for image
      `Rs. ${Number(item.amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`
    ]);

    autoTable(doc, {
      head: headers,
      body: data,
      startY: 50,
      theme: 'grid',
      headStyles: { fillColor: [193, 151, 37], textColor: 255, halign: 'center' },
      columnStyles: {
        0: { halign: 'center', cellWidth: 15 },
        1: { halign: 'center', cellWidth: 35 },
        2: { fontStyle: 'bold', cellWidth: 50 },
        3: { halign: 'center', cellWidth: 40 }, // Bill Image
        4: { halign: 'right', fontStyle: 'bold', cellWidth: 40 }
      },
      styles: { fontSize: 9, cellPadding: 4, valign: 'middle' },

      didParseCell: (data) => {
        // Set cell height for image column
        if (data.section === 'body' && data.column.index === 3) {
          data.cell.styles.minCellHeight = 35;
        }
      },

      didDrawCell: (data) => {
        if (data.section === 'body' && data.column.index === 3) {
          const rowData = this.filteredData[data.row.index];
          if (rowData && rowData.__img) {
            const imgW = 25;
            const imgH = 25;
            const x = data.cell.x + (data.cell.width - imgW) / 2;
            const y = data.cell.y + (data.cell.height - imgH) / 2;
            doc.addImage(rowData.__img, 'JPEG', x, y, imgW, imgH);
          } else if (rowData && rowData.document && rowData.document.toLowerCase().endsWith('.pdf')) {
            doc.setFontSize(8);
            doc.setTextColor(150);
            doc.text('PDF Attached', data.cell.x + data.cell.width / 2, data.cell.y + data.cell.height / 2, { align: 'center' });
          }
        }
      }
    });

    // --- Footer / Total ---
    const finalY = (doc as any).lastAutoTable.finalY + 10;

    doc.setDrawColor(200);
    doc.setLineWidth(0.5);
    doc.rect(130, finalY, 60, 15);
    doc.setFontSize(10);
    doc.setTextColor(193, 151, 37);
    doc.text('GRAND TOTAL:', 135, finalY + 9);
    doc.setFontSize(12);
    doc.setTextColor(0);
    doc.text(`Rs. ${this.totalExpense.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`, 185, finalY + 9, { align: 'right' });

    // Save PDF
    doc.save(fileName);
  }

  editExpense(id: any) {
    const basePath = this.router.url.includes('rm-portal') ? 'rm-portal/' : '';
    this.router.navigate([basePath + 'edit-expense/' + id]);
  }

  openDocument(url: string) {
    if (url) {
      window.open(url, '_blank');
    }
  }

  // --- SEARCH LOGIC ---
  filterTable() {
    const searchLower = this.searchText.toLowerCase().trim();

    if (searchLower) {
      // Filter the master list
      this.filteredData = this.expenseData.filter((item: any) => {
        const typeName = item.expense_type?.name || item.type || '';
        const searchTerms = [
          typeName,
          item.date || '',
          item.amount?.toString() || '',
          item.description || ''
        ].map(s => s.toLowerCase());

        return searchTerms.some(term => term.includes(searchLower));
      });
    } else {
      // If search is empty, revert to full list
      this.filteredData = [...this.expenseData];
    }

    // 1. Recalculate Total based on search results
    this.calculateTotal();

    // 2. Reset to Page 1 for new search results
    this.currentPage = 1;

    // 3. Re-setup pagination
    this.setupPagination();
  }

  calculateTotal() {
    this.totalExpense = this.filteredData.reduce((sum: number, exp: any) => {
      return sum + Number(exp.amount || 0);
    }, 0);
  }

  // --- PAGINATION LOGIC ---
  setupPagination() {
    this.totalPages = Math.ceil(this.filteredData.length / this.itemsPerPage);
    this.updatePaginatedList();
  }

  updatePaginatedList() {
    const start = (this.currentPage - 1) * this.itemsPerPage;
    const end = start + this.itemsPerPage;
    this.paginatedData = this.filteredData.slice(start, end);
  }

  goToPage(page: number) {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.updatePaginatedList();
    }
  }

  goToPreviousPage() {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.updatePaginatedList();
    }
  }

  goToNextPage() {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.updatePaginatedList();
    }
  }

  // Helper for pagination UI (creates array [1, 2, 3...])
  get pageNumbers(): number[] {
    return Array(this.totalPages).fill(0).map((_, i) => i + 1);
  }

  onItemsPerPageChange() {
    this.currentPage = 1;
    this.setupPagination();
  }

  // --- ACTIONS ---
  deleteExpense(id: any) {
    if (confirm("Are you sure you want to delete this expense?")) {
      this.expenseService.deleteExpense(id).subscribe((response: any) => {
        this.notificationService.showSuccess("Expense deleted successfully", "success");
        this.getExpenseData(); // Refresh data
      });
    }
  }
}